import React, { useState, useEffect } from 'react';

// ==========================================
// 1. 组件内部样式 (可直接复制到全局CSS文件)
// ==========================================
const popupStyles = `
  .info-popup-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    width: 18px;
    height: 18px;
    margin-left: 6px;
    border-radius: 50%;
    border: 1px solid #cbd5e0;
    color: #718096;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
    background-color: #f7fafc;
    transition: all 0.2s ease-in-out;
    vertical-align: middle;
    padding: 0;
    line-height: 1;
    user-select: none;
    flex-shrink: 0; 
  }

  .info-popup-trigger:hover {
    background-color: #3182ce;
    border-color: #3182ce;
    color: #ffffff;
    // transform: scale(1.1);
    box-shadow: 0 2px 5px rgba(49, 130, 206, 0.3);
  }

  .info-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: popupFadeIn 0.2s ease-out;
  }

  .info-popup-card {
    background: #ffffff;
    width: 90%;
    max-width: 460px;
    border-radius: 10px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    animation: popupSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }

  .info-popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #edf2f7;
  }

  .info-popup-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .info-popup-close {
    background: none;
    border: none;
    font-size: 22px;
    color: #a0aec0;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: color 0.2s;
  }

  .info-popup-close:hover {
    color: #e53e3e;
  }

  .info-popup-body {
    padding: 20px;
    font-size: 14px;
    color: #4a5568;
    line-height: 1.6;
    text-align: left;
    max-height: 60vh;
    overflow-y: auto;
  }

  .info-popup-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 20px;
    background-color: #f8fafc;
    border-top: 1px solid #edf2f7;
  }

  .info-popup-btn {
    padding: 8px 18px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid transparent;
  }

  .info-popup-btn-cancel {
    background-color: #ffffff;
    border-color: #cbd5e0;
    color: #4a5568;
  }

  .info-popup-btn-cancel:hover {
    background-color: #f1f5f9;
  }

  .info-popup-btn-confirm {
    background-color: #3182ce;
    color: #ffffff;
  }

  .info-popup-btn-confirm:hover {
    background-color: #2b6cb0;
    box-shadow: 0 2px 8px rgba(49, 130, 206, 0.4);
  }

  @keyframes popupFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes popupSlideUp {
    from { opacity: 0; transform: translateY(30px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

// ==========================================
// 2. 通用组件 InfoPopup
// ==========================================
export interface InfoPopupProps {
    /** 弹窗标题，默认 "提示" */
    title?: React.ReactNode;
    /** 弹窗内容，支持文本或 JSX */
    content: React.ReactNode;
    /** 确认按钮文案，默认 "我知道了" */
    confirmText?: string;
    customTrigger?: React.ReactNode;
}

const InfoPopup: React.FC<InfoPopupProps> = ({
    title = "提示",
    content,
    confirmText = "我知道了",
    customTrigger,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    // 弹窗打开时锁定背景滚动
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    return (
        <>
            <style>{popupStyles}</style>

            {/* 触发按钮：如果传了 customTrigger 用它，否则用默认的圆圈问号 */}
            <span onClick={() => setIsOpen(true)} style={{ display: 'inline-flex', cursor: 'pointer' }}>
                {customTrigger || (
                    <div className="info-popup-trigger" title="查看说明">
                        ?
                    </div>
                )}
            </span>

            {/* 弹窗主体 */}
            {isOpen && (
                <div className="info-popup-overlay" onClick={() => setIsOpen(false)}>
                    <div className="info-popup-card" onClick={(e) => e.stopPropagation()}>

                        <div className="info-popup-header">
                            <h3 className="info-popup-title">
                                {/* 一个简单的提示图标 */}
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3182ce" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                                {title}
                            </h3>
                            <button className="info-popup-close" onClick={() => setIsOpen(false)}>
                                &times;
                            </button>
                        </div>

                        <div className="info-popup-body">
                            {content}
                        </div>

                        <div className="info-popup-footer">

                            <button className="info-popup-btn info-popup-btn-confirm" onClick={() => setIsOpen(false)}>
                                {confirmText}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </>
    );
};

export default InfoPopup;